/**
 * Sample Checkout JavaScript - Eosera Shopify Integration
 * 
 * This script handles the custom checkout process for healthcare professional samples,
 * bypassing the normal Shopify checkout to avoid payment requirements for free products.
 */

class EoseraSampleCheckout {
    static initialized = false;
    static sampleCart = [];
    static config = {
        apiBaseUrl: 'https://api.icepick.co/npiregistry',
        debugMode: false
    };

    constructor() {
        this.cart = this.loadCart();
        this.init();
    }

    init() {
        if (this.initialized) return;

        console.log('🏥 Initializing Eosera Sample Checkout');

        this.loadCartDisplay();
        this.bindEvents();
        this.validateForm();
        this.populateHiddenFields();
        
        // Load subscription data from localStorage (saved from product page)
        this.loadSubscriptionFromStorage();

        this.initialized = true;
    }

    loadCart() {
        try {
            const cartData = localStorage.getItem('eosera_sample_cart');
            return cartData ? JSON.parse(cartData) : [];
        } catch (error) {
            console.error('Error loading cart:', error);
            return [];
        }
    }

    saveCart() {
        try {
            localStorage.setItem('eosera_sample_cart', JSON.stringify(this.cart));
            console.log('Cart saved to localStorage:', this.cart);
            this.loadCartDisplay();
            this.populateHiddenFields(); // Update hidden fields when cart changes
        } catch (error) {
            console.error('Error saving cart:', error);
        }
    }

    getCartData() {
        return this.cart;
    }

    addToCart(product) {
        // Normalize product data
        const normalizedProduct = {
            id: product.id || product.variant_id || Date.now() + Math.random(),
            variant_id: product.variant_id || product.id,
            title: product.title || 'Sample Product',
            variant_title: product.variant_title || product.variant || '',
            image: product.image || '',
            quantity: product.quantity || 1,
            price: 0, // Samples are always free
            npi_number: product.npi_number || product.properties?.npi_number,
            practice_name: product.practice_name || product.properties?.practice_name,
            practice_type: product.practice_type || product.properties?.provider_type || product.properties?.practice_type,
            samples_requested: product.samples_requested || product.properties?.samples_requested,
            verified_provider: product.verified_provider || product.properties?.verified_provider || 'true',
            timestamp: new Date().toISOString()
        };

        // Check if product already exists (by variant_id and npi_number)
        const existingIndex = this.cart.findIndex(
            item => item.variant_id === normalizedProduct.variant_id && 
                    item.npi_number === normalizedProduct.npi_number
        );

        if (existingIndex > -1) {
            // Update existing item with new data
            this.cart[existingIndex] = { ...this.cart[existingIndex], ...normalizedProduct };
            console.log('Updated existing cart item:', this.cart[existingIndex]);
        } else {
            // Add new item
            this.cart.push(normalizedProduct);
            console.log('Added new cart item:', normalizedProduct);
        }

        this.saveCart();
        this.showAddedMessage(normalizedProduct);
    }

    removeFromCart(itemId) {
        this.cart = this.cart.filter(item => item.id !== itemId);
        this.saveCart();
    }

    clearCart() {
        this.cart = [];
        this.saveCart();
        console.log('Cart cleared');
    }

    loadCartDisplay() {
        const cartData = this.getCartData();
        const cartContainer = document.getElementById('sample-cart-items');
        const cartCount = document.getElementById('sample-cart-count');

        if (!cartContainer) return;

        if (cartData.length === 0) {
            // Show empty cart message with Browse Samples button
            cartContainer.innerHTML = `
                <div class="empty-cart-message">
                    <p>Your sample cart is empty</p>
                    <a href="/products/sample-product" class="btn btn-secondary">
                        Browse Samples
                    </a>
                </div>
            `;
            if (cartCount) cartCount.textContent = '(0)';
            
            // Disable submit button when cart is empty
            this.updateSubmitButtonState(false);
            return;
        }

        // Show cart items
        cartContainer.style.display = 'block';
        if (cartCount) cartCount.textContent = `(${cartData.length})`;

        cartContainer.innerHTML = cartData.map((item, index) => {
            console.log('Rendering cart item:', item);
            
            // Improved image handling with better fallbacks
            let imageHTML = '';
            let imageUrl = item.image;
            
            // If no image URL, try to find variant-specific image
            if (!imageUrl && item.variant_title) {
                imageUrl = this.findVariantImage(item.variant_title, item.variant_id);
            }
            
            if (imageUrl && imageUrl !== '') {
                // Clean up image URL if it's a background-image CSS value
                if (imageUrl.includes('url(')) {
                    const match = imageUrl.match(/url\(["']?(.*?)["']?\)/);
                    if (match) imageUrl = match[1];
                }
                
                imageHTML = `
                    <img src="${imageUrl}" 
                         alt="${item.variant_title || item.title || 'Sample Product'}" 
                         style="width: 100%; height: 100%; object-fit: cover; border-radius: 4px;" 
                         onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';" />
                    <div style="width: 100%; height: 100%; background: #f8f9fa; display: none; align-items: center; justify-content: center; font-size: 12px; color: #6c757d; border-radius: 4px;">
                        No Image
                    </div>
                `;
            } else {
                imageHTML = `
                    <div style="width: 100%; height: 100%; background: #f8f9fa; display: flex; align-items: center; justify-content: center; font-size: 12px; color: #6c757d; border-radius: 4px;">
                        No Image
                    </div>
                `;
            }

            return `
                <div class="sample-cart-item" data-item-index="${index}">
                    <div class="sample-item-image">
                        ${imageHTML}
                    </div>
                    <div class="sample-item-details">
                        <div class="sample-item-title">${item.variant_title || item.title || 'Sample Product'}</div>
                        ${item.npi_number ? `<div class="sample-item-meta">NPI: ${item.npi_number}</div>` : ''}
                        ${item.practice_type ? `<div class="sample-item-meta">Type: ${item.practice_type}</div>` : ''}
                        ${item.practice_name ? `<div class="sample-item-meta">Practice: ${item.practice_name}</div>` : ''}
                    </div>
                    <button class="sample-item-remove" onclick="window.EoseraSampleCheckout.removeFromSampleCart(${index})" title="Remove item">×</button>
                </div>
            `;
        }).join('');

        // Auto-populate hidden fields if they exist
        this.populateHiddenFields();
        
        // Enable submit button when cart has items
        this.updateSubmitButtonState(true);
    }

    // Helper method to find variant-specific images
    findVariantImage(variantTitle, variantId) {
        console.log('🔍 Finding variant image for:', variantTitle, 'ID:', variantId);
        
        // Method 1: Try to find from product_variations if available
        if (window.product_variations && Array.isArray(window.product_variations)) {
            const variantData = window.product_variations.find(v => v.id == variantId);
            if (variantData && variantData.featured_image && variantData.featured_image.src) {
                console.log('🖼️ Found image from product_variations:', variantData.featured_image.src);
                return variantData.featured_image.src;
            }
        }

        // Method 2: Try to find variant-specific gallery by handle
        if (variantTitle) {
            // Create handle from variant title (same logic as in main-product.liquid)
            const variantHandle = variantTitle.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
            console.log('🏷️ Looking for gallery with handle:', `gallery-${variantHandle}`);
            
            // Try exact handle match first
            let variantGallery = document.querySelector(`#gallery-${variantHandle}`);
            
            // If not found, try alternative patterns
            if (!variantGallery) {
                console.log('🔍 Trying alternative handles...');
                const altHandle = variantTitle.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
                variantGallery = document.querySelector(`#gallery-${altHandle}`);
                console.log('🏷️ Trying alternative handle:', `gallery-${altHandle}`);
            }
            
            if (variantGallery) {
                console.log('✅ Found variant gallery');
                const galleryImage = variantGallery.querySelector('.product_gallery_image_src, .swiper-slide img, img');
                if (galleryImage) {
                    const imageUrl = galleryImage.src || galleryImage.style.backgroundImage?.match(/url\(["']?(.*?)["']?\)/)?.[1];
                    console.log('🖼️ Found image in variant gallery:', imageUrl);
                    return imageUrl;
                }
            } else {
                console.log('❌ Variant gallery not found');
                
                // Try to find images by matching variant name in filename
                console.log('🔍 Trying to find images by filename pattern...');
                const allImages = document.querySelectorAll('.product_gallery_slider img, .product-gallery img, .product__media img');
                const variantKey = variantTitle.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
                
                for (const img of allImages) {
                    const imgSrc = img.src || '';
                    const filename = imgSrc.split('/').pop()?.toLowerCase() || '';
                    console.log('🔍 Checking image:', filename, 'for pattern:', variantKey);
                    
                    if (filename.includes(variantKey) || filename.includes(variantKey.replace('-', '_'))) {
                        console.log('🖼️ Found variant image by filename:', imgSrc);
                        return imgSrc;
                    }
                }
            }
        }

        // Method 3: Fallback to main product image
        console.log('🔍 Using fallback product image...');
        const fallbackSelectors = [
            '.product_gallery_slider .swiper-slide img',
            '.product-image img',
            '.featured-image img',
            '.product__media img',
            '.product__media .media img',
            'img[data-product-image]',
            '.product-gallery img',
            'main img[src*="product"]',
            'img[alt*="product"]',
            'img[class*="product"]'
        ];
        
        let fallbackImage = '';
        for (const selector of fallbackSelectors) {
            const img = document.querySelector(selector);
            if (img && img.src && !img.src.includes('placeholder') && !img.src.includes('loading')) {
                fallbackImage = img.src;
                console.log('🖼️ Found fallback image:', fallbackImage);
                break;
            }
        }
        
        if (!fallbackImage) {
            console.log('🖼️ No fallback image found');
        }
        
        return fallbackImage;
    }

    showAddedMessage(product) {
        // Create temporary success message
        const message = document.createElement('div');
        message.className = 'sample-added-message';
        message.innerHTML = `
            <div style="
                position: fixed;
                top: 20px;
                right: 20px;
                background: #27ae60;
                color: white;
                padding: 15px 20px;
                border-radius: 8px;
                box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                z-index: 10000;
                animation: slideIn 0.3s ease-out;
            ">
                ${product.title} added to sample cart!
            </div>
        `;

        document.body.appendChild(message);

        // Remove message after 3 seconds
        setTimeout(() => {
            if (message.parentNode) {
                message.parentNode.removeChild(message);
            }
        }, 3000);
    }

    // Setup form event handlers
    bindEvents() {
        // Submit form handler
        const submitButton = document.getElementById('sampleCheckoutSubmit');
        if (submitButton) {
            submitButton.addEventListener('click', (e) => {
                e.preventDefault();
                this.submitOrder();
            });
        }

        // Real-time form validation
        const form = document.getElementById('sampleCheckoutForm');
        if (form) {
            form.addEventListener('input', () => {
                this.validateForm();
            });
        }

        // Close success modal
        const closeModalButton = document.getElementById('closeSuccessModal');
        if (closeModalButton) {
            closeModalButton.addEventListener('click', () => {
                this.closeSuccessModal();
            });
        }

        // Sample cart management
        window.EoseraSampleCheckout = {
            addToSampleCart: this.addToSampleCart.bind(this),
            removeFromSampleCart: this.removeFromSampleCart.bind(this)
        };

        // Appstle Subscription Widget Event Listeners
        this.bindAppstleEvents();
    }

    // Bind Appstle subscription widget events
    bindAppstleEvents() {
        // In checkout page, we don't need to detect widget changes
        // Subscription data is loaded from localStorage (set on product page)
        console.log('📻 Appstle events binding skipped - using localStorage data');
    }



    // Update UI to show subscription information (read-only)
    updateSubscriptionDisplay(isSubscription, sellingPlanId) {
        console.log('🔄 Updating subscription display:', { isSubscription, sellingPlanId });
        
        const subscriptionSection = document.getElementById('subscription-info-section');
        const subscriptionDisplayText = document.getElementById('subscription-display-text');
        
        if (!subscriptionSection || !subscriptionDisplayText) {
            console.warn('⚠️ Subscription display elements not found in DOM');
            return;
        }

        if (isSubscription && sellingPlanId) {
            // Get subscription frequency name
            const frequencyName = this.getSubscriptionFrequencyName(sellingPlanId);
            
            // Update display for subscription
            subscriptionDisplayText.textContent = `🔄 ${frequencyName}`;
            subscriptionSection.style.display = 'block';
            
            console.log('✅ Updated subscription display - Recurring Order:', frequencyName);
        } else {
            // One-time order
            subscriptionDisplayText.textContent = '📦 One-Time Order';
            subscriptionSection.style.display = 'block';
            
            console.log('✅ Updated subscription display - One-Time Order');
        }
    }

    // Get user-friendly subscription frequency name
    getSubscriptionFrequencyName(sellingPlanId) {
        const frequencyMap = {
            '7060029728': 'Monthly Subscription',
            '7060062496': 'Quarterly Subscription', 
            '7060095264': 'Semiannual Subscription'
        };
        
        return frequencyMap[sellingPlanId] || 'Subscription';
    }

    validateForm() {
        let isValid = true;
        const errors = [];

        // Get form fields
        const fullName = document.getElementById('full_name');
        const email = document.getElementById('email');
        const phone = document.getElementById('phone');
        const city = document.getElementById('city');
        const state = document.getElementById('state');
        const zipCode = document.getElementById('zip_code');
        const country = document.getElementById('country');

        // Validate full name
        if (!fullName || !fullName.value.trim()) {
            this.showFieldError(fullName, 'Please enter your full name');
            isValid = false;
        } else {
            this.hideFieldError(fullName);
        }

        // Validate email
        if (!email || !email.value.trim()) {
            this.showFieldError(email, 'Please enter your email address');
            isValid = false;
        } else if (!this.isValidEmail(email.value)) {
            this.showFieldError(email, 'Please enter a valid email address');
            isValid = false;
        } else {
            this.hideFieldError(email);
        }

        // Validate phone
        if (!phone || !phone.value.trim()) {
            this.showFieldError(phone, 'Please enter your phone number');
            isValid = false;
        } else if (!this.isValidPhone(phone.value)) {
            this.showFieldError(phone, 'Please enter a valid phone number');
            isValid = false;
        } else {
            this.hideFieldError(phone);
        }

        // Address validation removed - using address_line_2 as optional field

        // Validate city
        if (!city || !city.value.trim()) {
            this.showFieldError(city, 'Please enter your city');
            isValid = false;
        } else {
            this.hideFieldError(city);
        }

        // Validate state
        if (!state || !state.value.trim()) {
            this.showFieldError(state, 'Please enter your state/province');
            isValid = false;
        } else {
            this.hideFieldError(state);
        }

        // Validate zip code
        if (!zipCode || !zipCode.value.trim()) {
            this.showFieldError(zipCode, 'Please enter your ZIP/postal code');
            isValid = false;
        } else {
            this.hideFieldError(zipCode);
        }

        // Validate country
        if (!country || !country.value) {
            this.showFieldError(country, 'Please select your country');
            isValid = false;
        } else {
            this.hideFieldError(country);
        }

        return isValid;
    }

    // Validate email format
    validateEmail(email) {
        const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
        return isValid;
    }

    // Validate phone format
    validatePhone(phone) {
        // Remove all non-digit characters
        const cleaned = phone.replace(/\D/g, '');
        // Check if it's between 10-15 digits (international format)
        return cleaned.length >= 10 && cleaned.length <= 15;
    }

    // Validate NPI format
    validateNPIFormat(npi) {
        return /^\d{10}$/.test(npi);
    }

    // Submit sample order to API
    async submitOrder() {
        // First, check if cart has items
        const cartData = this.getCartData();
        if (!cartData || cartData.length === 0) {
            alert('Your sample cart is empty. Please add some samples before submitting your request.');
            return;
        }

        if (!this.validateForm()) {
            return;
        }

        const submitButton = document.getElementById('sampleCheckoutSubmit');
        const originalText = submitButton.textContent;

        try {
            // Show loading state
            submitButton.disabled = true;
            submitButton.textContent = 'Processing your request...';

            // Get structured form data for PHP backend
            const orderData = this.getStructuredOrderData();

            console.log('Submitting order with structured data:', orderData);

            // Submit to backend
            const response = await fetch('https://api.icepick.co/npiregistry/create-sample-order.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify(orderData)
            });

            const result = await response.json();
            console.log('Order submission result:', result);

            if (result.success) {
                // Clear cart and show success
                this.clearCart();
                
                // Clear subscription data from localStorage to reset for next visit
                localStorage.removeItem('eosera_subscription_plan');
                console.log('🧹 Cleared subscription data from localStorage');
                
                this.showSuccessModal();
                
                // Reset form
                document.getElementById('sampleCheckoutForm').reset();
            } else {
                throw new Error(result.message || 'Failed to submit order');
            }

        } catch (error) {
            console.error('Order submission error:', error);
            
            // Extract and show specific error message
            const userFriendlyMessage = this.extractErrorMessage(error);
            this.showErrorAlert(userFriendlyMessage);
            
        } finally {
            // Reset button state
            submitButton.disabled = false;
            submitButton.textContent = originalText;
        }
    }

    getFormData() {
        return {
            full_name: document.getElementById('full_name')?.value || '',
            email: document.getElementById('email')?.value || '',
            phone: document.getElementById('phone')?.value || '',
            address_line_2: document.getElementById('address_line_2')?.value || '',
            city: document.getElementById('city')?.value || '',
            state: document.getElementById('state')?.value || '',
            zip_code: document.getElementById('zip_code')?.value || '',
            country: document.getElementById('country')?.value || '',
            notes: document.getElementById('additional_notes')?.value || '',
            npi_number: document.getElementById('hidden_npi_number')?.value || '',
            practice_name: document.getElementById('hidden_practice_name')?.value || '',
            provider_type: document.getElementById('hidden_provider_type')?.value || ''
        };
    }

    // Get order data structured for PHP backend
    getStructuredOrderData() {
        const formData = this.getFormData();
        const cartData = this.getCartData();

        // Load subscription data from localStorage (set on product page)
        const subscriptionData = this.loadSubscriptionFromStorage();
        
        let isSubscription = false;
        let sellingPlanId = null;

        // First priority: Use localStorage data (most reliable)
        if (subscriptionData && subscriptionData.selling_plan_id) {
            isSubscription = subscriptionData.is_subscription;
            sellingPlanId = subscriptionData.selling_plan_id;
            console.log('📦 Using subscription data from localStorage:', subscriptionData);
        } else {
            // Fallback: Try to detect from hidden form fields
            const hiddenSellingPlanId = document.getElementById('hidden_selling_plan_id')?.value;
            const hiddenIsSubscription = document.getElementById('hidden_is_subscription')?.value;
            
            if (hiddenSellingPlanId && hiddenSellingPlanId !== '') {
                isSubscription = hiddenIsSubscription === 'true';
                sellingPlanId = hiddenSellingPlanId;
                console.log('📦 Using subscription data from hidden fields:', { isSubscription, sellingPlanId });
            } else {
                // Last resort: Try to detect from Appstle widget (if present)
                const subscriptionWrapper = document.querySelector('.appstle_subscription_wrapper_option.appstle-active-option');
                const isRecurringSelected = subscriptionWrapper && !subscriptionWrapper.querySelector('.appstle_one_time_details_wrapper');
                
                const planSelect = document.querySelector('.appstle_sub_widget select.appstle_select');
                const selectedPlanId = planSelect ? planSelect.value : null;
                
                const widget = document.querySelector('.appstle_sub_widget');
                const dataSelectedPlan = widget ? widget.getAttribute('data-selected-selling-plan') : null;

                if (isRecurringSelected && planSelect) {
                    isSubscription = true;
                    sellingPlanId = selectedPlanId || dataSelectedPlan;
                    console.log('📦 Using subscription data from Appstle widget:', { isSubscription, sellingPlanId });
                }
            }
        }

        console.log('📦 Final Order Data Subscription Info:');
        console.log('- Is Subscription:', isSubscription);
        console.log('- Selling Plan ID:', sellingPlanId);

        // Structure data as expected by create-sample-order.php
        const orderData = {
            customer: {
                name: formData.full_name,
                email: formData.email,
                phone: formData.phone
            },
            provider: {
                npi_number: formData.npi_number,
                practice_name: formData.practice_name,
                provider_type: formData.provider_type
            },
            shipping_address: {
                address1: formData.address_line_2 || '',
                address2: '',
                city: formData.city,
                province: formData.state,
                zip: formData.zip_code,
                country: formData.country
            },
            line_items: cartData.map(item => ({
                variant_id: item.variant_id,
                quantity: item.quantity || 1,
                title: item.title,
                variant_title: item.variant_title,
                price: '0.00',
                properties: {
                    _samples_requested: item.samples_requested || 'Yes',
                    _verified_provider: item.verified_provider || 'true'
                }
            })),
            notes: formData.notes || '',
            billing_address: {
                address1: formData.address_line_2 || '',
                address2: '',
                city: formData.city,
                province: formData.state,
                zip: formData.zip_code,
                country: formData.country
            },
            // Add subscription data
            selling_plan_id: sellingPlanId,
            is_subscription: isSubscription,
            products: this.getSelectedProducts() // Helper for Appstle metadata
        };

        return orderData;
    }

    // Helper method to get selected products for Appstle metadata
    getSelectedProducts() {
        const products = [];
        const cartData = this.getCartData();
        
        cartData.forEach(item => {
            const title = (item.variant_title || item.title || '').toUpperCase();
            if (title.includes('EAR PAIN')) {
                products.push('EAR PAIN MD');
            } else if (title.includes('EAR ITCH')) {
                products.push('EAR ITCH MD');
            } else if (title.includes('EAR WAX')) {
                products.push('EAR WAX MD');
            }
        });
        
        return products;
    }

    // Show success modal
    showSuccessModal() {
        const modal = document.getElementById('successModal');
        if (modal) {
            modal.style.display = 'flex';
        }
    }

    // Close success modal
    closeSuccessModal() {
        const modal = document.getElementById('successModal');
        if (modal) {
            modal.style.display = 'none';
        }
        
        // Optionally redirect to homepage or product page
        window.location.href = '/';
    }

    // Redirect to sample checkout page
    redirectToCheckout() {
        window.location.href = '/pages/request-samples';
    }

    // Debug helper
    debug(...args) {
        if (this.config.debugMode) {
            console.log('🔍 [SampleCheckout Debug]', ...args);
        }
    }

    // Extract user-friendly error message from server response
    extractErrorMessage(error) {
        let message = error.message || 'Unknown error occurred';
        
        // Common error patterns and their user-friendly translations
        const errorTranslations = {
            // Phone number specific errors
            'phone: is invalid': 'Invalid phone number format. Please enter a valid phone number with area code (e.g., +1-234-567-8900 or (234) 567-8900).',
            'customer.phone: has already been taken': 'This phone number is already registered with another healthcare provider account. Please use a different phone number or contact support if this is your correct number.',
            'Phone number has already been taken': 'This phone number is already registered with another healthcare provider account. Please use a different phone number or contact support if this is your correct number.',
            'customer.phone: is invalid': 'Invalid phone number format. Please enter a valid phone number with area code (e.g., +1-234-567-8900 or (234) 567-8900).',
            
            // Email specific errors  
            'customer.email: has already been taken': 'This email address is already registered with another healthcare provider account. Please use a different email address or contact support if this is your correct email.',
            'email: has already been taken': 'This email address is already registered with another healthcare provider account. Please use a different email address or contact support if this is your correct email.',
            'customer.email: is invalid': 'Invalid email format. Please enter a valid email address (e.g., doctor@yourpractice.com).',
            'email: is invalid': 'Invalid email format. Please enter a valid email address (e.g., doctor@yourpractice.com).',
            
            // NPI specific errors
            'Invalid NPI number': 'The NPI number provided is not valid. NPI numbers must be exactly 10 digits. Please check and try again.',
            'NPI not found in registry': 'The NPI number was not found in the official CMS registry. Please verify the number is correct and belongs to an active healthcare provider.',
            
            // Address specific errors
            'shipping_address.zip: is invalid': 'Invalid ZIP code format. Please enter a valid ZIP code (e.g., 12345 or 12345-6789).',
            'shipping_address.province: is invalid': 'Invalid state/province. Please select a valid state from the dropdown.',
            'shipping_address.city: can\'t be blank': 'City is required. Please enter the city for your shipping address.',
            'shipping_address.address1: can\'t be blank': 'Street address is required. Please enter your complete shipping address.',
            
            // General field errors
            'Missing field': 'Please fill in all required fields marked with an asterisk (*).',
            'Invalid email format': 'Please enter a valid email address (e.g., doctor@yourpractice.com).',
            'Invalid phone format': 'Please enter a valid phone number with area code (e.g., +1-234-567-8900).',
            'No line items provided': 'Your sample cart is empty. Please go back and add some samples to your cart first.',
            
            // API connectivity errors
            'Failed to verify NPI': 'Unable to verify your NPI number due to a connection issue. Please check your internet connection and try again.',
            'Network error': 'Connection problem detected. Please check your internet connection and try again.',
            'Failed to fetch': 'Unable to connect to our servers. Please check your internet connection and try again.',
            
            // HTTP status errors
            'Shopify API error: HTTP 401': 'Authentication error occurred. Please refresh the page and try again, or contact support if the problem persists.',
            'Shopify API error: HTTP 403': 'Access denied. Please contact support for assistance.',
            'Shopify API error: HTTP 404': 'The requested product was not found. Please refresh the page and try again.',
            'Shopify API error: HTTP 422': 'Some of the information provided is invalid. Please review all fields and try again.',
            'Shopify API error: HTTP 429': 'Too many requests were sent. Please wait a moment and try again.',
            'Shopify API error: HTTP 500': 'Server error occurred. Please try again in a few moments or contact support if the problem persists.'
        };

        // Look for specific error patterns (exact matches first for precision)
        for (const [pattern, translation] of Object.entries(errorTranslations)) {
            if (message.includes(pattern)) {
                return translation;
            }
        }

        // Handle complex Shopify error patterns with specific field extraction
        // Pattern: "Shopify API error: HTTP 422 - field_name: error_description"
        const shopifyComplexErrorMatch = message.match(/Shopify API error: HTTP 422 - (.+?):\s*(.+?)(?:,|$)/);
        if (shopifyComplexErrorMatch) {
            const [, field, errorMsg] = shopifyComplexErrorMatch;
            
            // Specific field error handlers
            const fieldErrorHandlers = {
                'phone': () => {
                    if (errorMsg.includes('invalid')) {
                        return 'Invalid phone number format. Please enter a valid phone number with area code (e.g., +1-234-567-8900 or (234) 567-8900).';
                    }
                    if (errorMsg.includes('already been taken')) {
                        return 'This phone number is already registered with another healthcare provider account. Please use a different phone number or contact support if this is your correct number.';
                    }
                    return `Phone number error: ${errorMsg}. Please check your phone number format and try again.`;
                },
                'customer': () => {
                    if (errorMsg.includes('Phone number has already been taken')) {
                        return 'This phone number is already registered with another healthcare provider account. Please use a different phone number or contact support if this is your correct number.';
                    }
                    if (errorMsg.includes('Email has already been taken')) {
                        return 'This email address is already registered with another healthcare provider account. Please use a different email address or contact support if this is your correct email.';
                    }
                    return `Account error: ${errorMsg}. Please check your information and try again.`;
                },
                'email': () => {
                    if (errorMsg.includes('invalid')) {
                        return 'Invalid email format. Please enter a valid email address (e.g., doctor@yourpractice.com).';
                    }
                    if (errorMsg.includes('already been taken')) {
                        return 'This email address is already registered with another healthcare provider account. Please use a different email address or contact support if this is your correct email.';
                    }
                    return `Email error: ${errorMsg}. Please check your email address and try again.`;
                }
            };

            const handler = fieldErrorHandlers[field.toLowerCase()];
            if (handler) {
                return handler();
            }

            // Generic field error with friendly field names
            const friendlyFieldNames = {
                'phone': 'Phone number',
                'email': 'Email address',
                'customer': 'Account information',
                'shipping_address': 'Shipping address',
                'first_name': 'First name',
                'last_name': 'Last name',
                'city': 'City',
                'province': 'State/Province',
                'zip': 'ZIP code',
                'country': 'Country'
            };
            
            const friendlyField = friendlyFieldNames[field.toLowerCase()] || field;
            return `${friendlyField} error: ${errorMsg}. Please correct this information and try again.`;
        }

        // Extract standard Shopify field.subfield errors (fallback for unhandled cases)
        const shopifyFieldErrorMatch = message.match(/(\w+\.\w+): (.+?)(?:,|$)/);
        if (shopifyFieldErrorMatch) {
            const [, field, errorMsg] = shopifyFieldErrorMatch;
            
            // Enhanced field error handling with specific messages
            const fieldErrorHandlers = {
                'customer.phone': (msg) => {
                    if (msg.includes('invalid')) {
                        return 'Invalid phone number format. Please enter a valid phone number with area code (e.g., +1-234-567-8900 or (234) 567-8900).';
                    }
                    if (msg.includes('has already been taken')) {
                        return 'This phone number is already registered with another healthcare provider account. Please use a different phone number or contact support if this is your correct number.';
                    }
                    return `Phone number: ${msg}. Please check your phone number and try again.`;
                },
                'customer.email': (msg) => {
                    if (msg.includes('invalid')) {
                        return 'Invalid email format. Please enter a valid email address (e.g., doctor@yourpractice.com).';
                    }
                    if (msg.includes('has already been taken')) {
                        return 'This email address is already registered with another healthcare provider account. Please use a different email address or contact support if this is your correct email.';
                    }
                    return `Email address: ${msg}. Please check your email address and try again.`;
                },
                'shipping_address.zip': (msg) => {
                    if (msg.includes('invalid')) {
                        return 'Invalid ZIP code format. Please enter a valid ZIP code (e.g., 12345 or 12345-6789).';
                    }
                    return `ZIP code: ${msg}. Please enter a valid ZIP code.`;
                },
                'shipping_address.province': (msg) => {
                    return 'Invalid state/province. Please select a valid state from the dropdown menu.';
                }
            };

            const handler = fieldErrorHandlers[field];
            if (handler) {
                return handler(errorMsg);
            }

            // Fallback with friendly field names
            const fieldNames = {
                'customer.phone': 'Phone number',
                'customer.email': 'Email address',
                'customer.first_name': 'First name',
                'customer.last_name': 'Last name',
                'shipping_address.address1': 'Street address',
                'shipping_address.city': 'City',
                'shipping_address.province': 'State/Province',
                'shipping_address.zip': 'ZIP/Postal code',
                'shipping_address.country': 'Country',
                'line_items.variant_id': 'Product selection'
            };
            
            const friendlyField = fieldNames[field] || field.replace(/\w+\./, '');
            return `${friendlyField}: ${errorMsg}. Please correct this information and try again.`;
        }

        // Handle generic HTTP error codes with helpful context
        if (message.includes('422')) {
            // Try to extract more specific info from 422 errors
            if (message.includes('phone')) {
                return 'There was a problem with the phone number provided. Please check the format and try again (e.g., +1-234-567-8900).';
            }
            if (message.includes('email')) {
                return 'There was a problem with the email address provided. Please check the format and try again.';
            }
            if (message.includes('customer')) {
                return 'There was a problem with your account information. This phone number or email may already be registered. Please use different contact information or contact support.';
            }
            if (message.includes('address')) {
                return 'There was a problem with the shipping address provided. Please check all address fields and try again.';
            }
            return 'Some of the information provided is invalid. Please review all fields carefully and try again. Common issues: phone number format, duplicate email/phone, or missing required fields.';
        }

        if (message.includes('500')) {
            return 'Server error occurred. Please try again in a few moments or contact support if the problem persists.';
        }

        if (message.includes('503')) {
            return 'Our servers are temporarily unavailable. Please try again in a few minutes.';
        }

        if (message.includes('429')) {
            return 'Too many requests were sent. Please wait 30 seconds and try again.';
        }

        // Return the original message if it's already user-friendly
        if (message.length < 200 && !message.includes('Error:') && !message.includes('HTTP')) {
            return message;
        }

        // Fallback message
        return 'An error occurred while processing your request. Please try again or contact support if the problem persists.';
    }

    // Show error alert in a more elegant way
    showErrorAlert(message) {
        // Try to use a custom modal if available, otherwise fall back to alert
        const existingModal = document.getElementById('errorModal');
        
        if (existingModal) {
            // Update existing error modal
            const messageElement = existingModal.querySelector('.error-message');
            if (messageElement) {
                messageElement.textContent = message;
                existingModal.style.display = 'flex';
                return;
            }
        }

        // Create a temporary error modal
        const modal = document.createElement('div');
        modal.id = 'errorModal';
        modal.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(0, 0, 0, 0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 10001;
        `;

        const modalContent = document.createElement('div');
        modalContent.style.cssText = `
            background: white;
            padding: 30px;
            border-radius: 12px;
            max-width: 500px;
            margin: 20px;
            box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
            text-align: center;
        `;

        modalContent.innerHTML = `
            <div style="color: #e74c3c; font-size: 48px; margin-bottom: 20px;">⚠️</div>
            <h3 style="color: #333; margin: 0 0 15px 0; font-size: 20px;">Request Failed</h3>
            <p class="error-message" style="color: #666; margin: 0 0 25px 0; line-height: 1.5;">${message}</p>
            <button onclick="this.closest('#errorModal').remove()" style="
                background: #e74c3c;
                color: white;
                border: none;
                padding: 12px 24px;
                border-radius: 6px;
                font-size: 16px;
                cursor: pointer;
                transition: background 0.3s ease;
            " onmouseover="this.style.background='#c0392b'" onmouseout="this.style.background='#e74c3c'">
                Close
            </button>
        `;

        modal.appendChild(modalContent);
        document.body.appendChild(modal);

        // Auto-remove after 10 seconds
        setTimeout(() => {
            if (modal.parentNode) {
                modal.remove();
            }
        }, 10000);

        // Close on background click
        modal.addEventListener('click', (e) => {
            if (e.target === modal) {
                modal.remove();
            }
        });
    }

    // Update submit button state based on cart contents
    updateSubmitButtonState(hasItems) {
        const submitButton = document.getElementById('sampleCheckoutSubmit');
        if (!submitButton) return;

        if (hasItems) {
            submitButton.disabled = false;
            submitButton.textContent = 'Request Samples';
            submitButton.style.opacity = '1';
            submitButton.style.cursor = 'pointer';
        } else {
            submitButton.disabled = true;
            submitButton.textContent = 'Add samples to cart first';
            submitButton.style.opacity = '0.6';
            submitButton.style.cursor = 'not-allowed';
        }
    }

    populateHiddenFields() {
        const cartData = this.getCartData();
        
        // Get NPI data from the first item in cart (all should have the same NPI data)
        if (cartData.length > 0) {
            const firstItem = cartData[0];
            
            // Populate hidden fields
            const hiddenNpiNumber = document.getElementById('hidden_npi_number');
            const hiddenPracticeName = document.getElementById('hidden_practice_name');
            const hiddenProviderType = document.getElementById('hidden_provider_type');
            
            if (hiddenNpiNumber && firstItem.npi_number) {
                hiddenNpiNumber.value = firstItem.npi_number;
            }
            
            if (hiddenPracticeName && firstItem.practice_name) {
                hiddenPracticeName.value = firstItem.practice_name;
            }
            
            if (hiddenProviderType && firstItem.practice_type) {
                hiddenProviderType.value = firstItem.practice_type;
            }
            
            console.log('Hidden fields populated with NPI data:', {
                npi_number: firstItem.npi_number,
                practice_name: firstItem.practice_name,
                practice_type: firstItem.practice_type
            });
        }
    }

    addToSampleCart(productData) {
        console.log('Adding to sample cart (instance):', productData);
        
        // Use the existing addToCart method which has duplicate checking
        this.addToCart(productData);
    }

    removeFromSampleCart(index) {
        console.log('Removing item at index:', index);
        if (index >= 0 && index < this.cart.length) {
            this.cart.splice(index, 1);
            this.saveCart();
            this.loadCartDisplay(); // This will call updateSubmitButtonState automatically
            console.log('Item removed, cart updated:', this.cart);
        }
    }

    showFieldError(field, message) {
        if (!field) return;
        
        const validationDiv = field.parentNode.querySelector('.validation-message');
        if (validationDiv) {
            validationDiv.textContent = message;
            validationDiv.style.opacity = '1';
        }
        
        field.classList.add('error');
    }

    hideFieldError(field) {
        if (!field) return;
        
        const validationDiv = field.parentNode.querySelector('.validation-message');
        if (validationDiv) {
            validationDiv.style.opacity = '0';
        }
        
        field.classList.remove('error');
    }

    isValidEmail(email) {
        const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
        return isValid;
    }

    isValidPhone(phone) {
        // Remove all non-digit characters
        const cleaned = phone.replace(/\D/g, '');
        // Check if it's between 10-15 digits (international format)
        return cleaned.length >= 10 && cleaned.length <= 15;
    }

    // Load subscription data from localStorage (saved from product page)
    loadSubscriptionFromStorage() {
        try {
            const storedData = localStorage.getItem('eosera_subscription_plan');
            if (storedData) {
                const subscriptionData = JSON.parse(storedData);
                console.log('💾 Loaded subscription data from localStorage:', subscriptionData);
                
                // Check if data is not too old (max 1 hour)
                const dataAge = new Date() - new Date(subscriptionData.timestamp);
                const maxAge = 60 * 60 * 1000; // 1 hour in milliseconds
                
                                 if (dataAge < maxAge) {
                     this.subscriptionData = subscriptionData;
                     this.updateSubscriptionDisplay(
                         subscriptionData.is_subscription, 
                         subscriptionData.selling_plan_id
                     );
                     this.updateSubscriptionHiddenFields(subscriptionData);
                     return subscriptionData;
                } else {
                    console.log('⏰ Subscription data expired, clearing...');
                    localStorage.removeItem('eosera_subscription_plan');
                }
            }
        } catch (error) {
            console.error('❌ Error loading subscription data:', error);
        }
        
                 // Default to one-time order if no valid data
         this.subscriptionData = {
             selling_plan_id: null,
             is_subscription: false,
             frequency_name: 'One-Time Order'
         };
         
         this.updateSubscriptionDisplay(false, null);
         this.updateSubscriptionHiddenFields(this.subscriptionData);
         return this.subscriptionData;
    }

    // Get subscription data (from localStorage or default)
    getSubscriptionData() {
        if (!this.subscriptionData) {
            return this.loadSubscriptionFromStorage();
        }
        return this.subscriptionData;
    }

    // Update hidden fields with subscription data
    updateSubscriptionHiddenFields(subscriptionData) {
        console.log('🔄 Updating subscription hidden fields:', subscriptionData);
        
        // Update hidden fields
        this.updateHiddenField('hidden_selling_plan_id', subscriptionData.selling_plan_id || '');
        this.updateHiddenField('hidden_is_subscription', subscriptionData.is_subscription ? 'true' : 'false');
        this.updateHiddenField('hidden_subscription_frequency', subscriptionData.frequency_name || '');
        
        console.log('✅ Subscription hidden fields updated');
    }

    // Helper method to update hidden field values
    updateHiddenField(fieldId, value) {
        const field = document.getElementById(fieldId);
        if (field) {
            field.value = value;
            console.log(`📝 Updated ${fieldId} = ${value}`);
        } else {
            console.warn(`⚠️ Hidden field not found: ${fieldId}`);
        }
    }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
    // Make class available globally first
    window.EoseraSampleCheckoutClass = EoseraSampleCheckout;
    
    // Only initialize full instance if we're on the sample checkout page
    if (document.getElementById('sampleCheckoutForm')) {
        const instance = new EoseraSampleCheckout();
        
        // Make instance and its methods globally available
        window.EoseraSampleCheckout = instance;
        
        console.log('✅ Eosera Sample Checkout initialized');
    } else {
        // If not on checkout page, create a minimal global interface for other pages
        window.EoseraSampleCheckout = {
            // Storage methods available on all pages
            getCartData: function() {
                try {
                    const cartData = localStorage.getItem('eosera_sample_cart');
                    return cartData ? JSON.parse(cartData) : [];
                } catch (error) {
                    console.error('Error loading cart:', error);
                    return [];
                }
            },
            
            saveCart: function(cart) {
                try {
                    localStorage.setItem('eosera_sample_cart', JSON.stringify(cart));
                    console.log('Cart saved to localStorage:', cart);
                } catch (error) {
                    console.error('Error saving cart:', error);
                }
            },
            
            addToSampleCart: function(productData) {
                console.log('Adding to sample cart (global):', productData);
                
                // Ensure we have the necessary data structure
                const item = {
                    id: productData.id || productData.variant_id,
                    variant_id: productData.variant_id || productData.id,
                    title: productData.title || 'Sample Product',
                    variant_title: productData.variant_title || productData.variant || '',
                    image: productData.image || '',
                    quantity: productData.quantity || 1,
                    price: 0, // Samples are always free
                    // Ensure NPI data is included for all products
                    npi_number: productData.npi_number || productData.properties?.npi_number,
                    practice_name: productData.practice_name || productData.properties?.practice_name,
                    practice_type: productData.practice_type || productData.properties?.provider_type || productData.properties?.practice_type,
                    samples_requested: productData.samples_requested || productData.properties?.samples_requested,
                    verified_provider: productData.verified_provider || productData.properties?.verified_provider || 'true'
                };

                // Get current cart
                const cart = this.getCartData();
                
                // Check if product already exists (by variant_id)
                const existingIndex = cart.findIndex(cartItem => 
                    cartItem.variant_id === item.variant_id && 
                    cartItem.npi_number === item.npi_number
                );

                if (existingIndex > -1) {
                    // Update existing item with new data (in case NPI data changed)
                    cart[existingIndex] = { ...cart[existingIndex], ...item };
                    console.log('Updated existing cart item:', cart[existingIndex]);
                } else {
                    // Add new item
                    cart.push(item);
                    console.log('Added new cart item:', item);
                }
                
                this.saveCart(cart);
                console.log('Sample cart updated (global):', cart);
            },
            
            redirectToCheckout: function() {
                window.location.href = '/pages/request-samples';
            },
            
            // Add empty init method for compatibility
            init: function() {
                console.log('Sample checkout init called from global interface');
                return this;
            },
            
            // Add removeFromSampleCart method for global compatibility
            removeFromSampleCart: function(index) {
                console.log('Removing item at index (global):', index);
                const cart = this.getCartData();
                if (index >= 0 && index < cart.length) {
                    cart.splice(index, 1);
                    this.saveCart(cart);
                    
                    // If we're on the checkout page, trigger a reload of the display
                    if (document.getElementById('sample-cart-items')) {
                        window.location.reload();
                    }
                    
                    console.log('Item removed, cart updated (global):', cart);
                }
            },
            
            // Add method to clear cart for debugging
            clearCart: function() {
                localStorage.removeItem('eosera_sample_cart');
                console.log('Sample cart cleared');
                
                // If we're on the checkout page, trigger a reload
                if (document.getElementById('sample-cart-items')) {
                    window.location.reload();
                }
            }
        };
        
        console.log('✅ Eosera Sample Checkout global interface ready');
    }
});

// Add styles for empty cart and remove buttons
const additionalStyles = `
<style>
.empty-cart-message {
    text-align: center;
    padding: 40px 20px;
    color: #666;
}

.empty-cart-message p {
    margin: 0 0 20px 0;
    font-size: 16px;
}

.btn-secondary {
    display: inline-block;
    padding: 12px 24px;
    background: #f8f9fa;
    color: #333;
    text-decoration: none;
    border-radius: 8px;
    border: 2px solid #e1e5e9;
    font-weight: 600;
    transition: all 0.3s ease;
}

.btn-secondary:hover {
    background: #e9ecef;
    border-color: #adb5bd;
}

.remove-item-btn {
    background: none;
    border: none;
    color: #dc3545;
    cursor: pointer;
    padding: 8px;
    border-radius: 4px;
    transition: all 0.3s ease;
}

.remove-item-btn:hover {
    background: #f8f9fa;
    color: #c82333;
}

.validation-message.checking {
    color: #17a2b8;
}

.validation-message.warning {
    color: #ffc107;
}
</style>
`;

document.head.insertAdjacentHTML('beforeend', additionalStyles); 